import * as React from 'react'
import NextLink from 'next/link'
import styled, { css } from 'styled-components'

import * as plume from '@ulule/owl-kit-components/next'

import * as cold from '@owl-nest/cold-data/client'
import { useHierarchyHref } from '@boutique/hooks'

type DesktopHierarchy = {
  tree: typeof cold.hierarchy.tree
  activeId?: string
}

export function DesktopHierarchy({ activeId, tree }: DesktopHierarchy) {
  return (
    <DesktopWrapper>
      <plume.styles.heading.XS>Catégories</plume.styles.heading.XS>
      <Tree>
        {tree.parents
          .slice()
          .reverse()
          // working from root to currently open tree (parents list reversed), we
          // build a nested <ul><li><ul><li>...</ul> structure from the inside to
          // the outside, adding more and more <ul><li>...</li><ul> around what we
          // got during the previous iteration. The first step of the iteration
          // (so the innermost <ul>) is what is passed in last parameter to the
          // `reduce` function. This first step is the list of direct children of
          // the currently open tree.
          .reduce((jsx, { id, name }) => {
            const active = id === activeId

            return (
              <List>
                <ListItem active={active}>
                  <Entry active={active} id={id} name={name} />
                  {jsx}
                </ListItem>
              </List>
            )
          }, <OpenTree tree={tree} activeId={activeId} />)}
      </Tree>
      <Separator />
    </DesktopWrapper>
  )
}

type OpenTreeProps = {
  tree: typeof cold.hierarchy.tree
  activeId?: string
}

function OpenTree({ tree, activeId }: OpenTreeProps) {
  const active = tree.id === activeId
  const listDisplayLimit = 8
  const [showAll, setShowAll] = React.useState(tree.children.length <= listDisplayLimit)

  // special case for root tree, we only display its children, not itself
  if (tree.type === '$$root$$') {
    return (
      <List>
        {tree.children.map(({ id, name }) => {
          const active = id === activeId
          return (
            <ListItem key={id} active={active}>
              <Entry active={active} name={name} id={id} />
            </ListItem>
          )
        })}
      </List>
    )
  }

  const activeChildrenIndex = tree.children.findIndex((child) => child.id === activeId)
  const activeFirstChildren =
    activeChildrenIndex === -1
      ? tree.children
      : [
          tree.children[activeChildrenIndex],
          ...tree.children.slice(0, activeChildrenIndex),
          ...tree.children.slice(activeChildrenIndex + 1),
        ]
  const children = showAll ? activeFirstChildren : activeFirstChildren.slice(0, listDisplayLimit)

  // for any other three, we display itself, and its children in an nested list
  return (
    <List>
      <ListItem active={active}>
        <Entry active={active} id={tree.id} name={tree.name} />
        <List>
          {children.map(({ id, name }) => {
            const active = id === activeId
            return (
              <ListItem key={id} active={active}>
                <Entry active={active} name={name} id={id} />
              </ListItem>
            )
          })}
          {!showAll && (
            <plume.LinkAsButton underlined onClick={() => setShowAll(true)}>
              Voir plus
            </plume.LinkAsButton>
          )}
        </List>
      </ListItem>
    </List>
  )
}

type EntryProps = {
  id: string
  name: string
  active: boolean
}

function Entry({ id, name, active }: EntryProps) {
  if (active) {
    return <plume.styles.copy.M>{name}</plume.styles.copy.M>
  }

  const url = useHierarchyHref(id)

  return (
    <plume.styles.copy.M as="div">
      <NextLink href={url}>{name}</NextLink>
    </plume.styles.copy.M>
  )
}

const Separator = styled.hr`
  background-color: ${plume.COLORS.GREY_SHADE_7};
  border: none;
  height: 1px;
  margin-bottom: 32px;
`

const DesktopWrapper = styled.div`
  display: none;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    display: block;
  }
`

const Tree = styled.div`
  border: 1px solid ${plume.COLORS.GREY_SHADE_5};
  border-radius: 4px;
  padding: 16px 0 20px;
  margin: 16px 0 30px;
`

const List = styled.ul`
  margin-left: 16px;

  ${plume.LinkAsButton} {
    padding: 0;
    font-size: 14px;
    margin-top: 10px;
  }
`

const ListItem = styled.li<{ active: boolean }>`
  & > ${plume.styles.copy.M} {
    margin: 0 0 10px;

    ${({ active }) => {
      if (active) {
        return css`
          font-weight: 600;
        `
      }
    }}
  }
`
